/**
 * Global hook for robust marker background sync.
 * Mounted in MainAppLayout so it runs as long as the user is authenticated.
 *
 * Triggers syncAllPendingMarkers via:
 * 1. On mount (app start / page refresh)
 * 2. Every 30 seconds (if online)
 * 3. On visibility change (tab becomes visible again)
 * 4. On Capacitor appStateChange (native app resume)
 */
import { useEffect, useRef } from "react";
import { syncAllPendingMarkers } from "@/services/data/markerSync";
import { App } from "@capacitor/app";
import { isNativePlatform } from "@/utils/platform";

const SYNC_INTERVAL_MS = 30_000; // 30 seconds

export function useMarkerAutoSync() {
  const isSyncingRef = useRef(false);

  useEffect(() => {
    const runSync = async () => {
      if (!navigator.onLine || isSyncingRef.current) return;
      isSyncingRef.current = true;
      try {
        const result = await syncAllPendingMarkers();
        if (result.synced > 0) {
          console.log(`[useMarkerAutoSync] ✅ ${result.synced} markers synced`);
        }
      } catch (e) {
        console.warn("[useMarkerAutoSync] sync error:", e);
      } finally {
        isSyncingRef.current = false;
      }
    };

    // 1. Sync on mount
    runSync();

    // 2. Periodic interval
    const interval = setInterval(runSync, SYNC_INTERVAL_MS);

    // 3. Visibility change (web)
    const onVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        runSync();
      }
    };
    document.addEventListener("visibilitychange", onVisibilityChange);

    // 4. Capacitor app state change (native)
    let removeAppStateListener: (() => void) | undefined;
    if (isNativePlatform()) {
      App.addListener("appStateChange", ({ isActive }) => {
        if (isActive) runSync();
      }).then((handle) => {
        removeAppStateListener = () => handle.remove();
      });
    }

    return () => {
      clearInterval(interval);
      document.removeEventListener("visibilitychange", onVisibilityChange);
      removeAppStateListener?.();
    };
  }, []);
}
